import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { WifiOff, RefreshCw, CloudOff, Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';

export function OfflineIndicator() {
  const { isOnline, wasOffline } = useOnlineStatus();
  const { pendingCount, failedCount, isSyncing, syncPending, clearFailed } = useOfflineSync();
  const [showSuccess, setShowSuccess] = useState(false);

  // Show success message briefly after syncing completes
  useEffect(() => {
    if (isOnline && wasOffline && pendingCount === 0 && failedCount === 0 && !isSyncing) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, pendingCount, failedCount, isSyncing]);

  // Show if offline, syncing, has pending/failed items, or showing success message
  const shouldShow = !isOnline || isSyncing || pendingCount > 0 || failedCount > 0 || showSuccess;

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className={cn(
            'fixed top-0 left-0 right-0 z-[100] flex items-center justify-center gap-2 pt-safe-status pb-2 px-4 text-sm font-medium',
            !isOnline
              ? 'bg-muted/95 text-muted-foreground backdrop-blur-sm border-b border-border'
              : isSyncing
              ? 'bg-primary/10 text-primary backdrop-blur-sm border-b border-primary/20'
              : failedCount > 0
              ? 'bg-destructive/10 text-destructive backdrop-blur-sm border-b border-destructive/20'
              : pendingCount > 0
              ? 'bg-warning/10 text-warning-foreground backdrop-blur-sm border-b border-warning/20'
              : 'bg-success/10 text-success backdrop-blur-sm border-b border-success/20'
          )}
        >
          {!isOnline ? (
            <>
              <WifiOff className="h-4 w-4" />
              <span>You're offline. Changes saved locally.</span>
              {pendingCount > 0 && (
                <span className="ml-1 rounded-full bg-background/50 px-2 py-0.5 text-xs">
                  {pendingCount} pending
                </span>
              )}
            </>
          ) : isSyncing ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Syncing changes...</span>
            </>
          ) : failedCount > 0 ? (
            <>
              <AlertTriangle className="h-4 w-4" />
              <span>
                {failedCount} change{failedCount > 1 ? 's' : ''} couldn't sync
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 h-6 px-2 text-xs active:bg-background/50"
                onClick={clearFailed}
              >
                Clear failed
              </Button>
            </>
          ) : pendingCount > 0 ? (
            <>
              <CloudOff className="h-4 w-4" />
              <span>{pendingCount} change{pendingCount > 1 ? 's' : ''} pending</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 h-6 px-2 text-xs active:bg-background/50"
                onClick={syncPending}
                disabled={isSyncing}
              >
                Sync now
              </Button>
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              <span>All changes synced</span>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
